import { useEffect, useState } from 'preact/hooks';
import { ArrowDown, ArrowUp, Check, Crown, DoorOpen, LogOut, MessageCircle, Search, Settings, Shield, Star, Trash, UserMinus, UserPlus, Users, X } from 'lucide-preact';
import { CLAN_CREATE_COST, CLAN_MAX_MEMBERS, type ClanEmblem, type ClanMember, type ClanSummary, type CreateClanRequest } from '@vm/shared';
import { containsProfanity } from '@vm/content';
import { api } from '../api/routes';
import { audio } from '../../game/audio/Audio';
import { replace, route } from '../state/nav';
import { clanMemberAction, createClan, disbandClan, joinClan, leaveClan, loadMyClan, updateClan } from '../state/socialActions';
import { me, myClan, presenceById, restricted, wallet } from '../state/store';
import { fmtNum, hex } from '../ui/format';
import { useAsync } from '../ui/hooks';
import { EMBLEM_ICONS, Icon } from '../ui/icons';
import {
  AsyncButton,
  Avatar,
  Bar,
  confirmDialog,
  EMBLEM_COLORS,
  EMBLEM_SHAPES,
  Emblem,
  Empty,
  ErrorState,
  Field,
  OnlineOnly,
  Price,
  Screen,
  Segmented,
  Spinner,
  Tabs,
} from '../ui/kit';
import { ChannelView } from './Chat';
import { openProfile } from './Profile';

const POLICY_LABEL: Record<ClanSummary['joinPolicy'], string> = { open: 'Open', request: 'Request to join', closed: 'Invite only' };

// Emblem builder --------------------------------------------------------------------------------------------------

export function EmblemBuilder(props: { value: ClanEmblem; onChange: (e: ClanEmblem) => void }) {
  const e = props.value;
  const set = (patch: Partial<ClanEmblem>): void => {
    audio.play('ui_click');
    props.onChange({ ...e, ...patch });
  };
  return (
    <div class="emblem-builder">
      <div class="emblem-preview">
        <Emblem emblem={e} size={112} />
      </div>
      <div class="emblem-options">
        <div class="emblem-row">
          <span class="ui-field-label">Shape</span>
          <div class="emblem-choices">
            {EMBLEM_SHAPES.map((s, i) => (
              <button key={s.name} class={`emblem-choice ${e.shape === i ? 'is-on' : ''}`} title={s.name} onClick={() => set({ shape: i })}>
                <svg viewBox="0 0 64 64" width="26" height="26">
                  <path d={s.d} fill="currentColor" />
                </svg>
              </button>
            ))}
          </div>
        </div>
        <div class="emblem-row">
          <span class="ui-field-label">Color</span>
          <div class="emblem-choices">
            {EMBLEM_COLORS.map((c, i) => (
              <button key={i} class={`emblem-swatch ${e.color === c ? 'is-on' : ''}`} style={{ background: hex(c) }} aria-label={`Color ${i + 1}`} onClick={() => set({ color: c })} />
            ))}
          </div>
        </div>
        <div class="emblem-row">
          <span class="ui-field-label">Icon</span>
          <div class="emblem-choices icons">
            {EMBLEM_ICONS.map((name, i) => (
              <button key={name} class={`emblem-choice ${e.icon === i ? 'is-on' : ''}`} title={name} onClick={() => set({ icon: i })}>
                <Icon name={name} size={20} />
              </button>
            ))}
          </div>
        </div>
        <div class="emblem-row">
          <span class="ui-field-label">Icon color</span>
          <div class="emblem-choices">
            {EMBLEM_COLORS.map((c, i) => (
              <button key={i} class={`emblem-swatch ${e.iconColor === c ? 'is-on' : ''}`} style={{ background: hex(c) }} aria-label={`Icon color ${i + 1}`} onClick={() => set({ iconColor: c })} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Browse / create -------------------------------------------------------------------------------------------------

function ClanRow(props: { c: ClanSummary }) {
  const c = props.c;
  const full = c.members >= c.maxMembers;
  return (
    <div class="clan-row">
      <Emblem emblem={c.emblem} size={52} />
      <div class="clan-row-text">
        <b>
          <span class="clan-tag">[{c.tag}]</span> {c.name}
        </b>
        <small>
          Level {c.level} · {c.members}/{c.maxMembers} members · {POLICY_LABEL[c.joinPolicy]}
        </small>
      </div>
      <AsyncButton class={`sm ${c.joinPolicy === 'open' ? 'green' : ''}`} disabled={full || c.joinPolicy === 'closed'} onClick={() => joinClan(c)}>
        {full ? 'Full' : c.joinPolicy === 'open' ? (
          <>
            <DoorOpen size={16} /> Join
          </>
        ) : c.joinPolicy === 'request' ? (
          <>
            <UserPlus size={16} /> Request
          </>
        ) : (
          'Invite only'
        )}
      </AsyncButton>
    </div>
  );
}

function Browse() {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 350);
    return () => clearTimeout(t);
  }, [query]);
  const res = useAsync(async () => (await api.clans.search(debounced)).clans, [debounced]);
  return (
    <div class="clan-browse">
      <div class="ui-input-row search">
        <Search size={20} class="search-icon" />
        <input class="ui-input big" value={query} maxLength={24} placeholder="Search clans by name or tag" onInput={(e) => setQuery((e.currentTarget as HTMLInputElement).value)} />
      </div>
      {res.loading && !res.data ? (
        <Spinner />
      ) : res.error ? (
        <ErrorState message={res.error} onRetry={res.reload} />
      ) : !res.data?.length ? (
        <Empty icon={<Shield size={44} />} title={debounced ? 'No clans found' : 'No clans yet'} body="Why not start your own?" />
      ) : (
        <div class="clan-list">
          {res.data.map((c) => (
            <ClanRow key={c.id} c={c} />
          ))}
        </div>
      )}
    </div>
  );
}

function clanTextProblem(name: string, tag: string, desc: string): string | null {
  if (name.trim().length < 3) return 'Clan names need at least 3 characters.';
  if (!/^[A-Za-z0-9 _'-]{3,24}$/.test(name.trim())) return 'Use letters, numbers, spaces, _ or - (max 24).';
  if (!/^[A-Z0-9]{2,5}$/.test(tag)) return 'Tags are 2–5 letters or numbers.';
  if (containsProfanity(name) || containsProfanity(tag) || containsProfanity(desc)) return 'Please keep it friendly.';
  return null;
}

function CreateClan() {
  const [name, setName] = useState('');
  const [tag, setTag] = useState('');
  const [desc, setDesc] = useState('');
  const [policy, setPolicy] = useState<ClanSummary['joinPolicy']>('open');
  const [emblem, setEmblem] = useState<ClanEmblem>({ shape: 0, color: EMBLEM_COLORS[7], icon: 0, iconColor: EMBLEM_COLORS[14] });
  if (restricted.value) {
    return <Empty icon={<Shield size={44} />} title="Join a clan instead" body="Creating clans isn't available on your account, but you can join any open clan from the Browse tab." />;
  }
  const problem = clanTextProblem(name, tag, desc);
  const short = CLAN_CREATE_COST - wallet.value.coins;
  const req: CreateClanRequest = { name: name.trim(), tag, description: desc.trim(), emblem, joinPolicy: policy };
  return (
    <div class="clan-create">
      <div class="panel">
        <h3 class="panel-title">
          <Shield size={20} /> Emblem
        </h3>
        <EmblemBuilder value={emblem} onChange={setEmblem} />
      </div>
      <div class="panel">
        <h3 class="panel-title">Details</h3>
        <div class="form-grid">
          <Field label="Clan name">
            <input class="ui-input" value={name} maxLength={24} placeholder="Brick Breakers" onInput={(e) => setName((e.currentTarget as HTMLInputElement).value)} />
          </Field>
          <Field label="Tag">
            <input
              class="ui-input code-input"
              value={tag}
              maxLength={5}
              placeholder="BRKS"
              onInput={(e) => setTag((e.currentTarget as HTMLInputElement).value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
            />
          </Field>
        </div>
        <Field label="Description" hint="No personal info or links.">
          <textarea class="ui-input" rows={2} maxLength={200} value={desc} placeholder="We build forts and knock them down." onInput={(e) => setDesc((e.currentTarget as HTMLTextAreaElement).value)} />
        </Field>
        <Field label="Who can join">
          <Segmented
            options={[
              { value: 'open', label: 'Anyone' },
              { value: 'request', label: 'Request' },
              { value: 'closed', label: 'Invite only' },
            ]}
            value={policy}
            onChange={setPolicy}
          />
        </Field>
        {name && problem && <div class="form-error">{problem}</div>}
        <AsyncButton class="primary big" disabled={!!problem || short > 0} onClick={() => createClan(req)}>
          {short > 0 ? `Need ${fmtNum(short)} more` : 'Create clan'} <Price price={CLAN_CREATE_COST} currency="coins" />
        </AsyncButton>
      </div>
    </div>
  );
}

// My clan --------------------------------------------------------------------------------------------------------------

type ClanTab = 'roster' | 'chat' | 'requests' | 'settings';
const ROLE_RANK = { leader: 0, officer: 1, member: 2 } as const;

function RoleChip(props: { role: ClanMember['role'] }) {
  if (props.role === 'leader')
    return (
      <span class="role-chip leader">
        <Crown size={13} strokeWidth={3} /> Leader
      </span>
    );
  if (props.role === 'officer')
    return (
      <span class="role-chip officer">
        <Star size={13} strokeWidth={3} /> Officer
      </span>
    );
  return <span class="role-chip">Member</span>;
}

function Roster(props: { myRole: ClanMember['role'] }) {
  const c = myClan.value!;
  const myId = me.value?.id;
  const sorted = [...c.roster].sort((a, b) => ROLE_RANK[a.role] - ROLE_RANK[b.role] || b.contribution - a.contribution);
  return (
    <div class="flist">
      {sorted.map((m) => {
        const presence = presenceById.value[m.id]?.presence ?? m.presence;
        const canPromote = props.myRole === 'leader' && m.id !== myId && m.role !== 'leader';
        const canDemote = props.myRole === 'leader' && m.role === 'officer';
        const canKick = m.id !== myId && ((props.myRole === 'leader' && m.role !== 'leader') || (props.myRole === 'officer' && m.role === 'member'));
        return (
          <div key={m.id} class={`frow ${presence !== 'offline' ? 'is-online' : ''}`}>
            <button class="frow-who" onClick={() => openProfile(m.id, m.name)}>
              <Avatar name={m.name} presence={presence} size={44} />
              <span class="frow-text">
                <b>
                  {m.name}
                  <span class="frow-level">Lv {m.level}</span>
                </b>
                <small>
                  <RoleChip role={m.role} /> {fmtNum(m.contribution)} clan XP
                </small>
              </span>
            </button>
            <div class="frow-actions">
              {canPromote && (
                <AsyncButton
                  class="sm icon"
                  title={m.role === 'officer' ? 'Make leader' : 'Promote to officer'}
                  aria-label="Promote"
                  onClick={async () => {
                    if (m.role === 'officer' && !(await confirmDialog({ title: `Make ${m.name} the leader?`, body: 'You will become an officer.', confirm: 'Make leader' }))) return;
                    await clanMemberAction('promote', m.id, m.name);
                  }}
                >
                  <ArrowUp size={16} />
                </AsyncButton>
              )}
              {canDemote && (
                <AsyncButton class="sm icon" title="Demote to member" aria-label="Demote" onClick={() => clanMemberAction('demote', m.id, m.name)}>
                  <ArrowDown size={16} />
                </AsyncButton>
              )}
              {canKick && (
                <AsyncButton
                  class="sm icon danger"
                  title="Remove from clan"
                  aria-label="Remove from clan"
                  onClick={async () => {
                    if (await confirmDialog({ title: `Remove ${m.name}?`, body: 'They can ask to join again later.', confirm: 'Remove', danger: true })) await clanMemberAction('kick', m.id, m.name);
                  }}
                >
                  <UserMinus size={16} />
                </AsyncButton>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Requests() {
  const c = myClan.value!;
  if (!c.requests.length) return <Empty icon={<UserPlus size={44} />} title="No join requests" body="Requests from players who want to join show up here." />;
  return (
    <div class="flist">
      {c.requests.map((r) => (
        <div key={r.id} class="frow is-online">
          <button class="frow-who" onClick={() => openProfile(r.id, r.name)}>
            <Avatar name={r.name} size={44} />
            <span class="frow-text">
              <b>
                {r.name}
                <span class="frow-level">Lv {r.level}</span>
              </b>
            </span>
          </button>
          <div class="frow-actions">
            <AsyncButton class="sm green" onClick={() => clanMemberAction('accept', r.id, r.name)}>
              <Check size={16} /> Accept
            </AsyncButton>
            <AsyncButton class="sm" onClick={() => clanMemberAction('decline', r.id, r.name)}>
              <X size={16} />
            </AsyncButton>
          </div>
        </div>
      ))}
    </div>
  );
}

function ClanSettings(props: { myRole: ClanMember['role'] }) {
  const c = myClan.value!;
  const [desc, setDesc] = useState(c.description);
  const [policy, setPolicy] = useState(c.joinPolicy);
  const [emblem, setEmblem] = useState<ClanEmblem>(c.emblem);
  const bad = containsProfanity(desc);
  return (
    <div class="clan-create">
      <div class="panel">
        <h3 class="panel-title">
          <Shield size={20} /> Emblem
        </h3>
        <EmblemBuilder value={emblem} onChange={setEmblem} />
      </div>
      <div class="panel">
        <h3 class="panel-title">
          <Settings size={20} /> Settings
        </h3>
        <Field label="Description" error={bad ? 'Please keep it friendly.' : null}>
          <textarea class="ui-input" rows={3} maxLength={200} value={desc} onInput={(e) => setDesc((e.currentTarget as HTMLTextAreaElement).value)} />
        </Field>
        <Field label="Who can join">
          <Segmented
            options={[
              { value: 'open', label: 'Anyone' },
              { value: 'request', label: 'Request' },
              { value: 'closed', label: 'Invite only' },
            ]}
            value={policy}
            onChange={setPolicy}
          />
        </Field>
        <AsyncButton class="primary" disabled={bad} onClick={() => updateClan({ description: desc.trim(), joinPolicy: policy, emblem })}>
          Save changes
        </AsyncButton>
        {props.myRole === 'leader' && (
          <div class="danger-zone">
            <h4>Danger zone</h4>
            <AsyncButton
              class="danger"
              onClick={async () => {
                if (await confirmDialog({ title: `Disband ${c.name}?`, body: 'Everyone will be removed and the clan will be gone for good.', confirm: 'Disband', danger: true })) await disbandClan();
              }}
            >
              <Trash size={18} /> Disband clan
            </AsyncButton>
          </div>
        )}
      </div>
    </div>
  );
}

function MyClan() {
  const tab = (route.value.params.tab as ClanTab) || 'roster';
  const load = useAsync(() => loadMyClan(), [me.value?.clan?.id]);
  const c = myClan.value;
  if (!c) return load.error ? <ErrorState message={load.error} onRetry={load.reload} /> : <Spinner label="Loading your clan…" />;
  const myRole = c.roster.find((m) => m.id === me.value?.id)?.role ?? 'member';
  const staff = myRole !== 'member';
  const tabs = [
    { id: 'roster' as const, label: `Members ${c.members}/${c.maxMembers || CLAN_MAX_MEMBERS}`, icon: <Users size={16} /> },
    { id: 'chat' as const, label: 'Chat', icon: <MessageCircle size={16} /> },
    ...(staff ? [{ id: 'requests' as const, label: 'Requests', badge: c.requests.length }, { id: 'settings' as const, label: 'Settings', icon: <Settings size={16} /> }] : []),
  ];
  const active = tabs.some((t) => t.id === tab) ? tab : 'roster';
  return (
    <div class="myclan">
      <div class="clan-hero panel">
        <Emblem emblem={c.emblem} size={92} />
        <div class="clan-hero-text">
          <h2>
            <span class="clan-tag">[{c.tag}]</span> {c.name}
          </h2>
          <p>{c.description || 'No description yet.'}</p>
          <div class="clan-hero-meta">
            <span class="clan-level">Level {c.level}</span>
            <Bar value={c.xp} max={Math.max(1, c.xpToNext)} tone="blue" label={`${fmtNum(c.xp)} / ${fmtNum(c.xpToNext)} clan XP`} class="clan-xp" />
            {c.weeklyRank !== null && <span class="clan-rank">#{c.weeklyRank} this week</span>}
          </div>
        </div>
        <AsyncButton
          class="sm clan-leave"
          onClick={async () => {
            const leader = myRole === 'leader';
            const body = leader && c.members > 1 ? 'Promote someone else to leader first, or they will pick a new leader for you.' : 'You can join again later if the clan is open.';
            if (await confirmDialog({ title: `Leave ${c.name}?`, body, confirm: 'Leave', danger: true })) await leaveClan();
          }}
        >
          <LogOut size={16} /> Leave
        </AsyncButton>
      </div>
      <Tabs<ClanTab> value={active} onChange={(t) => replace('clan', { tab: t })} tabs={tabs} class="clan-tabs" />
      <div class="clan-tab-body" key={active}>
        {active === 'roster' && <Roster myRole={myRole} />}
        {active === 'chat' && (
          <div class="clan-chat panel">
            <ChannelView channel="clan" active />
          </div>
        )}
        {active === 'requests' && <Requests />}
        {active === 'settings' && <ClanSettings myRole={myRole} />}
      </div>
    </div>
  );
}

type NoClanTab = 'browse' | 'create';

export function ClanScreen() {
  const inClan = !!me.value?.clan;
  const tab = (route.value.params.tab as NoClanTab) || 'browse';
  return (
    <Screen
      title={inClan ? 'My Clan' : 'Clans'}
      icon={<Shield size={26} />}
      tabs={
        inClan ? undefined : (
          <Tabs<NoClanTab>
            value={tab === 'create' ? 'create' : 'browse'}
            onChange={(t) => replace('clan', { tab: t })}
            tabs={[
              { id: 'browse', label: 'Browse', icon: <Search size={16} /> },
              { id: 'create', label: 'Create', icon: <Shield size={16} /> },
            ]}
          />
        )
      }
    >
      <OnlineOnly feature="Clans">
        <div class="scr-scroll narrow">{inClan ? <MyClan /> : tab === 'create' ? <CreateClan /> : <Browse />}</div>
      </OnlineOnly>
    </Screen>
  );
}
